import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { StyledCreatorText, StyledInstanceCreateDateText } from './styles';
import { useUser } from '../../../contexts/UserContext';
import { buildPath, PRIVATE_ROUTE } from '../../../routes/routes';
import { formatDateTimeUTC } from '../../../utils/timeUtils';
import { getInitials, runningNodeExists } from '../../../utils/utils';
import Image from '../../elements/image/Image';
import Status from '../../elements/status/Status';
import Text from '../../elements/text/Text';
import UserTooltip from '../../elements/user-tooltip/UserTooltip';
import SecondaryChip from '../../sections/chips/SecondaryChip';
import UserAvatar from '../user-avatar/UserAvatar';

const SolrListItem = ({ instance }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { projectId } = useParams();

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const secondaryAction = (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <UserAvatar user={instance.author} />
      {instance.createDate && (
        <Box sx={{ ml: 1 }}>
          <Text caption>
            {t('createdAt', {
              ns: 'instance',
              date: formatDateTimeUTC(instance.createDate, user?.timeZone),
            })}
          </Text>
        </Box>
      )}
    </Box>
  );

  return (
    <ListItem secondaryAction={!isSmallScreen && secondaryAction}>
      <ListItemAvatar>
        <Image
          size="small"
          shape="rounded"
          alt={instance.name}
          src={instance?.icon}
        >
          {getInitials(instance.name)}
        </Image>
      </ListItemAvatar>

      <ListItemText>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Link
            component={RouterLink}
            to={buildPath({
              path: PRIVATE_ROUTE.INSTANCE_OVERVIEW,
              params: { projectId, instanceId: instance.id },
            })}
            color="inherit"
            underline="hover"
          >
            {instance.name}
          </Link>
          <Status
            instanceState={instance.status}
            solrRunning={runningNodeExists(instance.instances)}
          />
          <Box sx={{ ml: 2 }}>
            <SecondaryChip instance={instance} />
          </Box>
        </Box>
        {isSmallScreen && (
          <Box>
            <UserTooltip user={instance.author}>
              <StyledCreatorText component="a" caption aria-label="createdBy">
                {t('creator', { ns: 'instance' })}: {instance.author.publicName}
              </StyledCreatorText>
            </UserTooltip>
            <StyledInstanceCreateDateText caption>
              {t('createdAt', {
                ns: 'instance',
                date: formatDateTimeUTC(instance.createDate, user?.timeZone),
              })}
            </StyledInstanceCreateDateText>
          </Box>
        )}
      </ListItemText>
    </ListItem>
  );
};

SolrListItem.propTypes = {
  instance: PropTypes.object.isRequired,
};

export default SolrListItem;
